import { z } from 'zod';
import { CalendarDate } from './project.js';
import { GoalPeriodKind, PersonalGoal } from './performance-goal.js';
import { OkrAmbition } from './okr-template.js';
/**
 * Objective của một người trong một kỳ — APM-OKR6,
 * [docs/plan-okr-objective.md](../../../docs/plan-okr-objective.md).
 *
 * ## Vì sao tầng này phải có
 *
 * Trước APM-OKR6, `PersonalGoal` là một **Key Result** (một chỉ số + một đích)
 * và không có gì ở trên nó. Ba chỉ số cùng kỳ nằm cạnh nhau mà không gì nói
 * chúng là bằng chứng của **một** ý định — và khi ấy "OKR" chỉ còn chữ KR.
 *
 * ## ⚠️ Câu định tính, KHÔNG mang số
 *
 * Số nằm ở Key Result. Một Objective có "90%" trong tiêu đề là một KR viết nhầm
 * chỗ, và cuối kỳ sẽ có hai con số cho cùng một thứ.
 */
export const PersonalObjective = z.object({
    id: z.uuid(),
    ownerId: z.uuid(),
    periodKind: GoalPeriodKind,
    periodStart: CalendarDate,
    periodEnd: CalendarDate,
    title: z.string(),
    ambition: OkrAmbition,
    /** Ai viết câu này — chủ OKR sửa/xoá được Objective **mình tự viết** (2026-09-30). */
    createdById: z.uuid(),
    createdAt: z.iso.datetime(),
});
/**
 * Một Objective kèm các Key Result của nó — hình dạng giao diện vẽ.
 *
 * `objective` là `null` ở **nhóm mồ côi**: những KR không thuộc Objective nào.
 * Đó là một trạng thái hợp lệ và lâu dài (người dùng chốt 2026-09-22), không
 * phải dữ liệu chờ dọn — nên nhóm ấy có tên riêng chứ không bị tô cảnh báo.
 */
export const OkrObjectiveGroup = z.object({
    objective: PersonalObjective.nullable(),
    keyResults: z.array(PersonalGoal),
});
/**
 * Tiêu đề Objective **không được mang số** — cưỡng chế ở cả schema lẫn `finalize`.
 *
 * ⚠️ Bắt một chữ số đứng cạnh đơn vị hoặc dấu phần trăm, không bắt mọi chữ số:
 * "Rút ngắn vòng hồi quy" và "Giảm nợ kỹ thuật ở module 2" đều hợp lệ, còn
 * "Đạt 90% đúng hạn" thì không — con số ấy thuộc về một Key Result.
 *
 * ## ⚠️ KHÔNG dùng `\b` — nó sai hai lần với tiếng Việt
 *
 * Bản đầu viết `(%|percent|giờ|…)\b`, một `\b` chung cho cả nhóm. Hai chỗ hỏng:
 *
 * 1. `%` là ký tự **không phải chữ**, nên sau nó không bao giờ có ranh giới từ.
 *    Cả "Đạt 90%" lẫn "Đạt 90% đúng hạn" đều lọt — tức cửa hay gặp nhất chưa
 *    bao giờ đóng.
 * 2. `\w` chỉ tính chữ ASCII, nên `giờ` kết thúc bằng một ký tự **không phải
 *    chữ** với regex: "40 giờ mỗi tháng" cũng lọt. `ngày`, `lần`, `điểm` thì
 *    lại bắt đúng vì chúng kết thúc bằng `y`, `n`, `m`.
 *
 * Hỏng kiểu im lặng: hàm vẫn trả `true` cho vài đầu vào nên nhìn qua là nó chạy,
 * và một bộ test thử đúng một câu sẽ xanh.
 *
 * Thay bằng một lookahead "ký tự kế tiếp không phải chữ hay số" — nó đúng với
 * cả tiếng Việt có dấu, và vẫn không bắt `lan` trong "lang thang".
 */
const NUMERIC_TARGET = /\d\s*(?:%|(?:percent|giờ|gio|ngày|ngay|lần|lan|điểm|diem)(?![\p{L}\p{N}]))/iu;
export function objectiveTitleHasTarget(title) {
    return NUMERIC_TARGET.test(title);
}
export const CreatePersonalObjectiveInput = z
    .object({
    ownerId: z.uuid(),
    periodKind: GoalPeriodKind,
    periodStart: CalendarDate,
    periodEnd: CalendarDate,
    /** ⚠️ `.trim()` **trước** `.min()` — xem cạm bẫy ở CLAUDE.md. */
    title: z.string().trim().min(3).max(300),
    ambition: OkrAmbition,
})
    .superRefine((input, ctx) => {
    if (objectiveTitleHasTarget(input.title)) {
        ctx.addIssue({
            code: 'custom',
            path: ['title'],
            message: 'Objective không mang con số — con số thuộc về một Key Result',
        });
    }
});
export const UpdatePersonalObjectiveInput = z
    .object({
    title: z.string().trim().min(3).max(300).optional(),
    ambition: OkrAmbition.optional(),
})
    .superRefine((input, ctx) => {
    if (input.title !== undefined && objectiveTitleHasTarget(input.title)) {
        ctx.addIssue({
            code: 'custom',
            path: ['title'],
            message: 'Objective không mang con số — con số thuộc về một Key Result',
        });
    }
});
//# sourceMappingURL=okr-objective.js.map